import {
  CompanyConfig,
  getActiveCompanyConfig,
} from "domain/company/companyConfig";

import { ATTACHMENT_VARIANTS } from "data/products/attachments";
import { DOOR_WIDTH, doorCost } from "data/products/glassPanels";
import {
  Attachment,
  FinalCost,
  Material,
  MaterialType,
  Pannel,
  Province,
} from "types/canvasTypes";

function groupCosts(pannels: Pannel[]): FinalCost[] {
  const groupedPanels = new Map<string, Pannel[]>();

  pannels.forEach((panel) => {
    const key = `${panel.length}_${panel.isRPannel}`;
    if (groupedPanels.has(key)) {
      groupedPanels.get(key)!.push(panel);
    } else {
      groupedPanels.set(key, [panel]);
    }
  });

  const finalCosts: FinalCost[] = [];
  groupedPanels.forEach((panels) => {
    finalCosts.push({
      length: panels[0].length,
      price: panels[0].price,
      total: panels.length,
      isRPannel: panels[0].isRPannel,
    });
  });

  return finalCosts;
}

function getSpigotsCost(
  pannels: Pannel[],
  companyConfig: CompanyConfig
): Attachment[] {
  const attachmentsMap = companyConfig.catalog.attachments || ATTACHMENT_VARIANTS;
  return [
    {
      ...attachmentsMap[Material.Spigots],
      count: pannels.length * 2,
    },
  ];
}

function getDoorCost(
  pannels: Pannel[],
  companyConfig: CompanyConfig
): {
  doorCost: FinalCost;
  attachement: Attachment[];
} {
  const attachmentsMap = companyConfig.catalog.attachments || ATTACHMENT_VARIANTS;
  const configuredDoorWidth = companyConfig.catalog.doorWidth || DOOR_WIDTH;
  const configuredDoorCost = companyConfig.catalog.doorCost || doorCost;
  let count = 0;
  for (let i = 0; i < pannels.length; i++) {
    if (pannels[i].isRPannel) {
      count++;
    }
  }

  return {
    doorCost: {
      length: configuredDoorWidth,
      price: configuredDoorCost,
      total: count,
      isDoor: true,
    },
    attachement: [
      {
        ...attachmentsMap[Material.DoorLatch],
        count: 1,
      },
      {
        ...attachmentsMap[Material.FloorLock],
        count,
      },
      {
        ...attachmentsMap[Material.HingeKit],
        count,
      },
    ],
  };
}

function getpannelsAttachements(
  attachment: Attachment[],
  numberOfPannels: number,
  doubleDoor: boolean,
  companyConfig: CompanyConfig
): Attachment {
  const attachmentsMap = companyConfig.catalog.attachments || ATTACHMENT_VARIANTS;
  const attach90 =
    attachment.filter(
      (item) =>
        item.material === Material.Pannel90Attachemnt && item.count > 0
    )?.[0]?.count || 0;
  const attachAdjust =
    attachment.filter(
      (item) =>
        item.material === Material.AdjustableAttachement && item.count > 0
    )?.[0]?.count || 0;
  const wallAttachment =
    attachment.filter(
      (item) => item.material === Material.WallAttachment && item.count > 0
    )?.[0]?.count || 0;

  const numberOfPannelAttatchments =
    numberOfPannels - wallAttachment / 2 - attach90 - attachAdjust;

  return {
    ...attachmentsMap[Material.Pannel180Attachemnt],
    count: numberOfPannelAttatchments - (doubleDoor ? 1 : 0),
  };
}

export function getFinalCost(
  pannels: Pannel[],
  material: MaterialType,
  attachment: Attachment[],
  includeAttachements: boolean = true,
  doubleDoor: boolean,
  province: Province,
  companyConfig: CompanyConfig = getActiveCompanyConfig()
): {
  finalCost: FinalCost[];
  attachment: Attachment[];
  totalCost: number;
} {
  let totalCost = 0;
  const finalCost = groupCosts(pannels).sort((a, b) => a.length - b.length);
  const { doorCost, attachement: doorAttachement } = getDoorCost(
    pannels,
    companyConfig
  );

  let temp: Attachment[] = [];
  if (includeAttachements) {
    temp = [...attachment];
  }
  temp.push(...getSpigotsCost(pannels, companyConfig));

  if (doorCost.total > 0) {
    finalCost.push(doorCost);
    temp.push(...doorAttachement);
  }

  if (includeAttachements) {
    temp.push(
      getpannelsAttachements(attachment, pannels.length, doubleDoor, companyConfig)
    );
  }

  for (let i = 0; i < finalCost.length; i++) {
    totalCost += finalCost[i].price.get(province)! * finalCost[i].total;
  }

  for (let i = 0; i < temp.length; i++) {
    if (material === MaterialType.Silver) {
      totalCost += temp[i].costSilver.get(province)! * temp[i].count;
    } else {
      totalCost += temp[i].costBlack.get(province)! * temp[i].count;
    }
  }

  return { finalCost, attachment: temp, totalCost };
}
